import {
  AlertTriangle,
  Globe,
  ShieldCheck,
  User,
  Users,
  ScanFace,
  Cpu,
  Eye,
  Activity,
  Scan,
} from "lucide-react";
import faceScanImage from "../../assets/MainImage/FaceAI.jpg";
import { useEffect, useState, useRef } from "react";

const proctoringFeatures = [
  {
    icon: <User className="h-4 w-4 text-blue-600" />,
    title: "Face Recognition",
    description:
      "Face verification before the exam starts, with continuous checking to confirm the right person is taking the exam",
  },
  {
    icon: <Users className="h-4 w-4 text-blue-600" />,
    title: "Multi-Person Detection",
    description: "Alerts when multiple faces are detected in the background",
  },
  {
    icon: <Globe className="h-4 w-4 text-blue-600" />,
    title: "Background Analysis",
    description:
      "Detects suspicious background activity or unauthorized objects",
  },
  {
    icon: <AlertTriangle className="h-4 w-4 text-blue-600" />,
    title: "Violation Tracking",
    description:
      "Records all suspicious activities with timestamps for review and triggers automated notifications.",
  },
];

const howItWorks = [
  "Candidate enables camera before exam starts",
  "AI continuously monitors the video feed during exam",
  "Suspicious activities are flagged in real-time with timestamps",
  "Administrators receive instant alerts and detailed post-exam reports",
];

const ExamCamera = () => {
  const [scanProgress, setScanProgress] = useState(0);
  const [scanActive, setScanActive] = useState(true);
  const [detectionPoints, setDetectionPoints] = useState<any>([]);

  useEffect(() => {
    // Simulate advanced scanning animation
    const interval = setInterval(() => {
      setScanProgress((prev) => {
        if (prev >= 100) {
          setScanActive(false);
          setTimeout(() => {
            setScanActive(true);
            setScanProgress(0);
            generateNewDetectionPoints();
          }, 800);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    return () => clearInterval(interval);
  }, []);

  const generateNewDetectionPoints = () => {
    const points = [];
    for (let i = 0; i < 12; i++) {
      points.push({
        id: Math.random().toString(36).substring(7),
        x: Math.random() * 80 + 10,
        y: Math.random() * 80 + 10,
        size: Math.random() * 3 + 1,
        active: Math.random() > 0.3,
      });
    }
    setDetectionPoints(points);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center px-6 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full shadow-lg mb-4">
            <Eye className="h-5 w-5 text-white mr-2" />
            <span className="text-white font-medium">
              AI ENABLED PROCTORING SYSTEM
            </span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Intelligent Examination{" "}
            <span className="text-blue-600">Surveillance</span>
          </h2>
          <div className="flex justify-center mb-4">
            <span className="inline-block w-28 h-[5px] bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full shadow-md "></span>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Next-generation computer vision and deep learning algorithms
            ensuring academic integrity
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left side - Advanced AI Scanning Visualization */}
          <div className="relative w-full h-[500px] flex items-center justify-center">
            <div className="relative w-full max-w-md h-[550px] rounded-2xl overflow-hidden border-2 border-blue-200/50 shadow-2xl bg-gray-900">
              {/* Face image with futuristic overlay */}
              <div
                className="absolute inset-0 bg-cover bg-center opacity-90"
                style={{ backgroundImage: `url(${faceScanImage})` }}
              />

              {/* Data grid overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-900/10 to-indigo-900/10">
                {/* Dynamic grid lines */}
                <div className="absolute inset-0 grid grid-cols-12 grid-rows-8">
                  {Array.from({ length: 96 }).map((_, i) => (
                    <div
                      key={i}
                      className="border border-blue-400/10 relative overflow-hidden"
                    >
                      <div
                        className={`absolute inset-0 bg-blue-500/20 transition-all duration-500 ease-in-out`}
                        style={{
                          opacity: Math.random() > 0.85 ? 0.6 : 0,
                          transform: `scale(${Math.random() > 0.9 ? 1.5 : 1})`,
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* AI detection points */}
                {detectionPoints.map((point: any) => (
                  <div
                    key={point.id}
                    className={`absolute rounded-full ${
                      point.active ? "bg-green-400/80" : "bg-red-400/80"
                    } shadow-lg ${
                      point.active ? "shadow-green-400/30" : "shadow-red-400/30"
                    }`}
                    style={{
                      left: `${point.x}%`,
                      top: `${point.y}%`,
                      width: `${point.size}px`,
                      height: `${point.size}px`,
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                ))}

                {/* Neural network connections */}
                <svg
                  className="absolute inset-0 w-full h-full"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {detectionPoints.slice(0, 6).map((point: any, i: any) => {
                    const nextPoint =
                      detectionPoints[(i + 1) % detectionPoints.length];
                    if (!nextPoint) return null;
                    return (
                      <line
                        key={`line-${point.id}`}
                        x1={`${point.x}%`}
                        y1={`${point.y}%`}
                        x2={`${nextPoint.x}%`}
                        y2={`${nextPoint.y}%`}
                        stroke={
                          point.active
                            ? "rgba(74, 222, 128, 0.4)"
                            : "rgba(248, 113, 113, 0.4)"
                        }
                        strokeWidth="0.5"
                        strokeDasharray="2 2"
                      />
                    );
                  })}
                </svg>

                {/* Scanning animation */}
                <div className="absolute inset-0 overflow-hidden">
                  <div
                    className={`absolute left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-blue-400 to-transparent shadow-lg shadow-blue-400/50 transition-all duration-75 linear`}
                    style={{
                      top: `${scanProgress}%`,
                      opacity: scanActive ? 1 : 0,
                    }}
                  />
                  <div
                    className={`absolute top-0 bottom-0 w-[2px] bg-gradient-to-b from-transparent via-blue-400 to-transparent shadow-lg shadow-blue-400/50 transition-all duration-75 linear`}
                    style={{
                      left: `${scanProgress}%`,
                      opacity: scanActive ? 1 : 0,
                    }}
                  />
                </div>

                {/* Status overlay */}
                <div className="absolute bottom-4 left-4 right-4 bg-black/50 rounded-lg p-3 backdrop-blur-sm border border-blue-400/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="h-2 w-2 rounded-full bg-green-400 mr-2 animate-pulse"></div>
                      <span className="text-xs font-mono text-blue-300">
                        AI_ACTIVE
                      </span>
                    </div>
                    <div className="text-xs font-mono text-blue-100">
                      SCAN: {Math.min(100, Math.floor(scanProgress))}%
                    </div>
                  </div>
                  <div className="mt-1 h-1 w-full bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-blue-400 to-green-400 transition-all duration-100"
                      style={{ width: `${scanProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Enhanced Features */}
          <div className="space-y-6">
            {/* Proctoring Features */}
            <div className="bg-white p-6 rounded-xl shadow-md border border-gray-200/70 backdrop-blur-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="mr-3 p-1.5 bg-blue-100 rounded-md">
                  <ShieldCheck className="h-5 w-5 text-blue-600" />
                </div>
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  Proctoring Features
                </span>
              </h3>
              <ul className="space-y-4">
                {proctoringFeatures.map((feature, index) => (
                  <li key={index} className="flex items-start group">
                    <div className="p-1.5 bg-gradient-to-br from-blue-100 to-blue-50 rounded-md mr-3 group-hover:scale-105 transition-transform">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 text-base">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {feature.description}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* How It Works */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200/40 backdrop-blur-sm shadow-md">
              <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <div className="mr-3 p-1.5 bg-blue-600/10 rounded-md">
                  <Scan className="h-5 w-5 text-blue-600" />
                </div>
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  How It Works
                </span>
              </h3>
              <ol className="space-y-3">
                {howItWorks.map((step, idx) => (
                  <li key={idx} className="flex items-start group">
                    <div className="relative mr-3">
                      <div className="absolute inset-0 bg-blue-600 rounded-full opacity-10 group-hover:opacity-20 transition-opacity"></div>
                      <div className="relative flex items-center justify-center w-7 h-7 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full text-white text-xs font-semibold">
                        {idx + 1}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExamCamera;
